import React from 'react';
import { 
  X, FileText, Download, DollarSign, CheckCircle, Link2 
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  calculatePaymentStatus 
} from '../utils/paymentUtils';
import { GSTSummary } from './GSTBreakdown';

/**
 * Shared component for viewing Closed Order details.
 * Used in AdminClosedOrders and AdminPayments.
 */
const ClosedOrderViewModal = ({ 
  show, 
  onClose, 
  order, 
  companySettings, 
  onDownloadPDF,
  onAddPayment,
  onCancelOrder,
  onUpdatePaymentStatus,
  onEditProducts,
  onConvertToInvoice,
  userRole
}) => {
  if (!show || !order) return null;

  // Helper functions
  const calculateOrderTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
      const discountAmount = baseAmount * ((item.discount || 0) / 100);
      const taxableAmount = baseAmount - discountAmount;
      const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
      return sum + (taxableAmount + gstAmount);
    }, 0);
  };

  const isPaymentCompleted = (order) => {
    const total = calculateOrderTotal(order.items || []);
    const paid = order.amountReceived || order.amount_received || order.paid_amount || 0;
    return paid >= (total - 0.1);
  };

  const isRefundCompleted = (order) => {
    return order.cancellation_payment_status === 'refunded' || order.cancellation_payment_status === 'Refunded';
  };

  const hasDiscounts = order.items?.some(item => (item.discount || 0) > 0) || false;

  const handleCopyLink = () => {
    if (!order.shareToken) {
      toast.error('Share token not available yet. Please reopen this modal.');
      return;
    }
    const guestLink = `${window.location.origin}/view/document/${order.shareToken}`;
    navigator.clipboard.writeText(guestLink)
      .then(() => toast.success('Guest link copied to clipboard!'))
      .catch((err) => toast.error('Failed to copy link'));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-[#B8985E] p-4 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Closed Order Details</h2>
              <p className="text-amber-50 text-sm opacity-90">#{order.closed_order_number || order.orderNumber || order.id || order._id}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-8 scrollbar-thin scrollbar-thumb-slate-200">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vendor Details */}
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 italic">
              <h3 className="text-[#B8985E] font-bold text-lg mb-4 border-b border-amber-100 pb-2">FROM</h3>
              <div className="space-y-1 text-slate-700">
                <p className="font-bold text-slate-900">{companySettings?.companyName}</p>
                <p className="text-sm">{companySettings?.addressLine1}</p>
                <p className="text-sm">{companySettings?.city}, {companySettings?.state} - {companySettings?.pincode}</p>
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm italic">
              <h3 className="text-amber-700 font-bold text-lg mb-4 border-b border-amber-100 pb-2">BILL TO</h3>
              <div className="space-y-1 text-slate-700">
                <p className="font-bold text-slate-900">{order.customer_name || 'Walk-in Customer'}</p>
                <p className="text-sm">{order.customer_address || order.customer?.address || 'N/A'}</p>
                <p className="text-sm font-medium mt-2">Phone: {order.customer_phone || order.customer?.phone || 'N/A'}</p>
                <p className="text-sm font-medium">GST: {order.customer_gst || order.customer?.gstNumber || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Doc Status Bar */}
          <div className="flex flex-wrap items-center gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
            <div className="flex items-center space-x-2">
              <span className="text-slate-500 text-sm font-medium">DATE:</span>
              <span className="text-slate-900 font-bold">{new Date(order.date || order.created_at).toLocaleDateString('en-IN')}</span>
            </div>
            <div className="h-4 w-px bg-slate-300 hidden sm:block"></div>
            <div className="flex items-center space-x-2">
              <span className="text-slate-500 text-sm font-medium">PAYMENT:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                calculatePaymentStatus(order.amountReceived || order.amount_received || 0, calculateOrderTotal(order.items || [])) === 'paid' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-amber-100 text-amber-800'
              }`}>
                {(order.paymentStatus || order.payment_status || 'PENDING').toUpperCase()}
              </span>
            </div>
          </div>

          {/* Cancellation Info */}
          {(order.cancellation_status === 'cancelled' || order.status === 'cancelled') && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <X className="h-5 w-5 text-red-600" />
                <h3 className="text-red-900 font-bold text-lg">Order Cancelled</h3>
              </div>
              <p className="text-red-700 text-sm mb-2"><span className="font-bold">Reason:</span> {order.cancellation_reason || 'N/A'}</p>
              <p className="text-red-700 text-sm italic font-bold">Refund Status: {order.cancellation_payment_status || 'Pending'}</p>
            </div>
          )}

          {/* Product Items Table */}
          <div className="border border-black overflow-hidden rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#B8985E] text-white">
                  <th className="p-3 text-left border-r border-amber-600 uppercase font-bold tracking-wider">S.No</th>
                  <th className="p-3 text-left border-r border-amber-600 uppercase font-bold tracking-wider">Description</th>
                  <th className="p-3 text-center border-r border-amber-600 uppercase font-bold tracking-wider">HSN</th>
                  <th className="p-3 text-center border-r border-amber-600 uppercase font-bold tracking-wider">QTY</th>
                  <th className="p-3 text-center border-r border-amber-600 uppercase font-bold tracking-wider">Rate</th>
                  {hasDiscounts && (
                    <th className="p-3 text-center border-r border-amber-600 uppercase font-bold tracking-wider">Disc %</th>
                  )}
                  <th className="p-3 text-right uppercase font-bold tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {order.items?.map((item, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}>
                    <td className="p-3 border-r border-black/10 text-center">{idx + 1}</td>
                    <td className="p-3 border-r border-black/10 font-medium">{item.product_name}</td>
                    <td className="p-3 border-r border-black/10 text-center">{item.hsn_code || '-'}</td>
                    <td className="p-3 border-r border-black/10 text-center font-bold">{item.quantity}</td>
                    <td className="p-3 border-r border-black/10 text-center">₹{item.unit_price?.toLocaleString('en-IN')}</td>
                    {hasDiscounts && (
                      <td className="p-3 border-r border-black/10 text-center text-amber-600 font-medium">{item.discount || 0}%</td>
                    )}
                    <td className="p-3 text-right font-bold">₹{(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sums */}
          <div className="flex justify-end pt-4">
            <div className="w-full max-w-md bg-slate-50 p-6 rounded-xl border-2 border-amber-200">
               <GSTSummary 
                  items={order.items || []}
                  buyerGST={order.customer_gst || ''}
                  companySettings={companySettings}
                  subtotal={calculateOrderTotal(order.items || [])}
                  className="space-y-2"
                />
                <div className="mt-4 pt-4 border-t border-amber-200 flex justify-between items-center">
                   <span className="text-slate-700 font-bold uppercase">Total Received:</span>
                   <span className="text-green-600 font-black text-xl">₹{(order.amountReceived || order.amount_received || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="mt-2 flex justify-between items-center text-red-600">
                   <span className="font-bold uppercase">Remaining:</span>
                   <span className="font-black text-lg">₹{(calculateOrderTotal(order.items || []) - (order.amountReceived || order.amount_received || 0)).toLocaleString('en-IN')}</span>
                </div>
            </div>
          </div>

          {/* Terms and Conditions & Material Description */}
          <div className="space-y-6 px-4 sm:px-8 pb-8">
            {order.termsAndConditions && (
              <div className="pt-6 border-t border-slate-200">
                <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide">Terms and Conditions</h4>
                <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed break-words">
                  {order.termsAndConditions}
                </div>
              </div>
            )}

            {order.materialDescription && (
              <div className="pt-6 border-t border-slate-200">
                <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide">Material Description</h4>
                <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed break-words">
                  {order.materialDescription}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Toolbar */}
        <div className="bg-slate-100 p-6 border-t border-slate-200 flex flex-wrap justify-end gap-3 shrink-0">
          {(order.cancellation_status === 'cancelled' || order.status === 'cancelled') ? (
            <>
               {onUpdatePaymentStatus && !isRefundCompleted(order) && (
                 <button onClick={onUpdatePaymentStatus} className="px-6 py-2 bg-amber-600 text-white rounded-lg font-bold">UPDATE REFUND STATUS</button>
               )}
            </>
          ) : (
            <>
              {onConvertToInvoice && (
                <button
                  onClick={onConvertToInvoice}
                  className="px-6 py-2 bg-[#0A5F38] text-white rounded-lg hover:bg-green-800 transition-all font-bold"
                >
                  CONVERT TO INVOICE
                </button>
              )}
              {userRole === 'admin' && onEditProducts && (
                <button
                  onClick={onEditProducts}
                  className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-all font-bold"
                >
                  EDIT ORDER
                </button>
              )}
              {!isPaymentCompleted(order) && onAddPayment && (
                <button
                  onClick={() => onAddPayment(order)}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-bold"
                >
                  ADD PAYMENT
                </button>
              )}
              {onCancelOrder && (
                <button onClick={onCancelOrder} className="px-6 py-2 bg-red-600 text-white rounded-lg font-bold">CANCEL ORDER</button>
              )}
            </>
          )}

          <button
            onClick={handleCopyLink}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-bold flex items-center space-x-2"
          >
            <Link2 className="h-4 w-4" />
            <span>COPY GUEST LINK</span>
          </button>

          {onDownloadPDF && (
            <button
               onClick={onDownloadPDF}
               className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all font-bold"
            >
               DOWNLOAD PDF
            </button>
          )}

          <button
            onClick={onClose}
            className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-all font-bold"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClosedOrderViewModal;
